import * as Sentry from '@sentry/browser';
import { createStore, createHook, Action } from 'react-sweet-state';

import { Repository } from 'src/components/types';
import { addStatuses, RepositoryBuildStatus } from 'src/redux/global/utils';

import { WorkspaceRepositoriesApi } from './requests';
import { WorkspaceRepositoriesState } from './types';

const addBuildStatusesToRepositories =
  (
    buildStatuses: RepositoryBuildStatus[]
  ): Action<WorkspaceRepositoriesState> =>
  ({ getState, setState }) => {
    setState({
      repositories: addStatuses(getState().repositories, buildStatuses),
    });
  };

const fetchBuildStatuses =
  (
    getBuildStatuses: WorkspaceRepositoriesApi['getBuildStatuses']
  ): Action<WorkspaceRepositoriesState> =>
  async ({ getState, setState, dispatch }) => {
    try {
      const repositoryUuids = getState()
        .repositories.map(repo => `uuid=${repo.uuid}`)
        .join('&');
      const buildStatuses = await getBuildStatuses(repositoryUuids);
      dispatch(addBuildStatusesToRepositories(buildStatuses));
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setState({
        buildStatusesFetched: true,
      });
    }
  };

const setRepositories =
  (
    { getBuildStatuses }: WorkspaceRepositoriesApi,
    repositories: Repository[]
  ): Action<WorkspaceRepositoriesState> =>
  ({ setState, dispatch }) => {
    setState({ repositories });
    dispatch(fetchBuildStatuses(getBuildStatuses));
  };

export const actions = {
  addBuildStatusesToRepositories,
  fetchBuildStatuses,
  setRepositories,
};

const initialState: WorkspaceRepositoriesState = {
  repositories: [],
  buildStatusesFetched: false,
};

const WorkspaceRepositoriesStore = createStore({
  initialState,
  actions,
});

export const useWorkspaceRepositories = createHook(WorkspaceRepositoriesStore);
